import React, { useState, useCallback, useMemo } from "react";
import { Menu, Layout } from "antd";
import { NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import SubMenu from "antd/lib/menu/SubMenu";
import { FaChartLine, FaRegSun } from "react-icons/fa";
import { ImTree } from "react-icons/im";
import { AiOutlineDashboard } from "react-icons/ai";
import { GiOnTarget } from "react-icons/gi";

import CustomScrollbars from "util/CustomScrollbars";
import SidebarLogo from "./SidebarLogo";
import { THEME_TYPE_LITE } from "../../constants/ThemeSetting";
import IntlMessages from "../../util/IntlMessages";
import { getRoles } from "../../util/getRoles";
import config from "../../permission/config.json";
import { getResource } from "../../util/getResourcePermission";
import OnboardingIcon from "./sidebarIcons/OnboardingIcon";
import TeamInfoIcon from "./sidebarIcons/TeamInfoIcon";
import AuditIcon from "./sidebarIcons/AuditIcon";
import PermissionmanagerIcon from "./sidebarIcons/PermissionmanagerIcon";
import {
  Account,
  Assistant_Reporting_Manager,
  Auditor,
  HR,
  Intern,
  Network_Team,
  Recruiter,
  Reporting_Manager,
  User,
  Receptionist
} from "./config";

const { Sider } = Layout;

// Constants
const DEPARTMENT_ARRAY = [
  "656eaa41679a64fd2b1dfa45",
  "656eaa41679a64fd2b1dfa47",
  "656eaa41679a64fd2b1dfa48",
  "656eaa41679a64fd2b1dfa5c"
];

const ROLE_CONFIGS = {
  "Admin": null, // Admin has custom logic
  "Reporting Manager": Reporting_Manager,
  "Assistance Reporting Manager": Assistant_Reporting_Manager,
  "Network Team": Network_Team,
  "User": User,
  "Auditor": Auditor,
  "HR": HR,
  "Intern": Intern,
  "Account Team": Account,
  "Recruiter": Recruiter,
  "Receptionist": Receptionist,
};

const SidebarContent = ({ sidebarCollapsed, setSidebarCollapsed, ...props }) => {
  const { themeType } = useSelector(({ settings }) => settings);
  const { pathname } = useSelector(({ common }) => common);
  const { authUser } = useSelector(({ auth }) => auth);
  const location = useLocation();
  const userdata = JSON.parse(localStorage.getItem("user_data"));

  const [openKeys, setOpenKeys] = useState([]);
  let submenuTimeout = null;

  // Memoized values
  const selectedKeys = useMemo(() => pathname.substr(1), [pathname]);
  const hasBillReimbursementAccess = useMemo(() =>
    DEPARTMENT_ARRAY.includes(authUser?.subdepartment_id?._id),
    [authUser?.subdepartment_id?._id]
  );

  // Event handlers
  const handleSubmenuEnter = useCallback((key) => {
    if (submenuTimeout) clearTimeout(submenuTimeout);
    setOpenKeys([key]);
  }, []);

  const handleSubmenuLeave = useCallback(() => {
    submenuTimeout = setTimeout(() => {
      setOpenKeys([]);
    }, 300);
  }, []);

  const handleOpenChange = useCallback((keys) => {
    setOpenKeys(keys.length ? [keys[keys.length - 1]] : []);
  }, []);

  const handleMouseEnter = useCallback((e) => {
    const submenuKey = e.key;
    if (submenuKey && !openKeys.includes(submenuKey)) {
      setOpenKeys([...openKeys, submenuKey]);
    }
  }, [openKeys]);

  const handleTitleClick = useCallback((key) => {
    setOpenKeys(openKeys.includes(key) ? [] : [key]);
  }, [openKeys]);

  // Check permissions
  const checkPermission = useCallback((item) => {
    return item?.permission?.length > 0
      ? getResource(item?.permission[0], ["read"])
      : true;
  }, []);

  const checkBillReimbursementPermission = useCallback((item) => {
    const billKey = item.key === 'Bill Reimbursement';
    return billKey && hasBillReimbursementAccess;
  }, [hasBillReimbursementAccess]);

  // Whether a menu item's path should be highlighted as active. Guards against legacy paths
  // (e.g. /dashboard, /goals, /kpi-kra) that are substrings of the newer /performance/* routes
  // (e.g. /performance/dashboard) from falsely lighting up while viewing a Performance page.
  const isMenuItemActive = useCallback((itemPath) => {
    if (!itemPath || !location.pathname) return false;
    if (location.pathname.startsWith("/performance/") && !itemPath.startsWith("/performance")) {
      return false;
    }
    return location.pathname.includes(itemPath);
  }, [location.pathname]);

  // Render menu item
  const renderMenuItem = useCallback((item) => {
    const permission = checkPermission(item);
    const billPermission = checkBillReimbursementPermission(item);

    if (!(permission || billPermission)) return null;

    return (
      <Menu.Item
        key={item.key}
        title={<span><IntlMessages id={item.title} /></span>}
        className={isMenuItemActive(item.path) ? "ant-menu-item-active" : ""}
      >
        <NavLink to={item.path}>
          {item.icon}
          <span>
            <IntlMessages id={item.title} />
          </span>
        </NavLink>
      </Menu.Item>
    );
  }, [location.pathname, checkPermission, checkBillReimbursementPermission, isMenuItemActive]);

  // Render submenu
  const renderSubMenu = useCallback((item) => {
    const permission = checkPermission(item);
    if (!permission) return null;

    const isActive = item.subMenuInfo?.some((sub) => isMenuItemActive(sub.path));

    return (
      <SubMenu
        key={item.key}
        icon={item.icon}
        title={item.title}
        popupClassName="right-submenu"
        onMouseEnter={() => handleSubmenuEnter(item.key)}
        onMouseLeave={handleSubmenuLeave}
        className={
          isActive
            ? `${item.className || "main-submenu"} full-width-hover ant-menu-item-active`
            : `${item.className || "main-submenu"} full-width-hover`
        }
        onTitleClick={() => handleTitleClick(item.key)}
      >
        {item.subMenuInfo.map((subItem) => {
          const subPermission = checkPermission(subItem);
          if (!subPermission) return null;

          return (
            <Menu.Item
              key={subItem.key}
              title={<span><IntlMessages id={subItem.title} /></span>}
              className={location.pathname === subItem.path ? "ant-menu-item-active" : ""}
            >
              <NavLink to={subItem.path}>
                {/* {subItem.icon} */}
                <span>
                  <IntlMessages id={subItem.title} />
                </span>
              </NavLink>
            </Menu.Item>
          );
        })}
      </SubMenu>
    );
  }, [location.pathname, checkPermission, handleSubmenuEnter, handleSubmenuLeave, handleTitleClick, isMenuItemActive]);

  // Render menu items from config
  const renderMenuFromConfig = useCallback((menuConfig) => {
    return menuConfig?.map((item) => {
      if (item.submenu) {
        return renderSubMenu(item);
      } else {
        return renderMenuItem(item);
      }
    });
  }, [renderSubMenu, renderMenuItem]);

  // Common menu props
  const menuProps = {
    mode: "vertical",
    openKeys,
    selectedKeys: [selectedKeys],
    theme: themeType === THEME_TYPE_LITE ? "lite" : "dark",
    triggerSubMenuAction: "hover",
    onMouseEnter: handleMouseEnter,
    onOpenChange: handleOpenChange,
  };

  // Render Admin menu (custom logic)
  const renderAdminMenu = () => (
    <Menu {...menuProps}>
      <Menu.Item
        key="Admin Dashboard"
        title={<span><IntlMessages id="Dashboard" /></span>}
        className={!location.pathname?.startsWith("/performance/") && location.pathname?.includes("/dashboard") ? "ant-menu-item-active" : ""}
      >
        <NavLink to="/dashboard">
          <AiOutlineDashboard />
          <span>
            <IntlMessages id="sidebar.Admin_Dashboard" />
          </span>
        </NavLink>
      </Menu.Item>

      {/* Your Info SubMenu */}
      <SubMenu
        key="sub15"
        title="Your Info"
        icon={<i className="fas fa-info-circle" />}
        popupClassName="right-submenu"
        onMouseEnter={() => handleSubmenuEnter("sub15")}
        onMouseLeave={handleSubmenuLeave}
        className={
          ["/my-in-out", "/leave-application", "/individual-rank"].some((path) =>
            location.pathname?.includes(path)
          )
            ? "main-submenu full-width-hover ant-menu-item-active"
            : "main-submenu full-width-hover"
        }
        onTitleClick={() => handleTitleClick("sub15")}
      >
        <Menu.Item
          key="MY-IN-OUT"
          title={<span><IntlMessages id="MY-IN-OUT" /></span>}
          className={location.pathname?.includes("/my-in-out") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/my-in-out">
            <span><IntlMessages id="sidebar.MY-IN-OUT" /></span>
          </NavLink>
        </Menu.Item>
        {/* Book Meeting Room moved out of this submenu — it's now a header
            button on the user and admin dashboards instead. */}
        <Menu.Item
          key="manage-leave-application"
          title={<span><IntlMessages id="sidebar.leave-application" /></span>}
          className={location.pathname?.includes("/leave-application") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/leave-application">
            <span><IntlMessages id="sidebar.leave-application" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="individual-ranking"
          title={<span><IntlMessages id="sidebar.individual-rank" /></span>}
          className={location.pathname?.includes("/individual-rank") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/individual-rank">
            <span><IntlMessages id="sidebar.individual-rank" /></span>
          </NavLink>
        </Menu.Item>
      </SubMenu>

      {/* Team Info SubMenu */}
      <SubMenu
        key="sub16"
        title="Team Info"
        icon={<TeamInfoIcon />}
        popupClassName="right-submenu"
        onMouseEnter={() => handleSubmenuEnter("sub16")}
        onMouseLeave={handleSubmenuLeave}
        className={
          ["/employee-master", "/employee-in-out", "/employee-camera-in-out", "/resignation-employee-list", "/admin-reports", "/employee-reports", "/leave-reports", "/attendence-reports", "/letter", "/tickets", "/admin-leave-approval", "/pending-task-list", "/admin-regularizatrion"].some((path) =>
            location.pathname?.includes(path)
          )
            ? "main-submenu full-width-hover ant-menu-item-active"
            : "main-submenu full-width-hover"
        }
        onTitleClick={() => handleTitleClick("sub16")}
      >
        {/* Team Info Menu Items */}
        <Menu.Item
          key="employee-master"
          title={<span><IntlMessages id="sidebar.employee-master" /></span>}
          className={location.pathname?.includes("/employee-master") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/employee-master">
            <span><IntlMessages id="sidebar.employee-master" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="employee-in-out"
          title={<span><IntlMessages id="sidebar.Employee-In-Out" /></span>}
          className={location.pathname?.includes("/employee-in-out") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/employee-in-out">
            <span><IntlMessages id="sidebar.Employee-In-Out" /></span>
          </NavLink>
        </Menu.Item>
        {/* Camera in/out UI removed — TimeChamp is the single attendance source.
            Commented (not deleted) for rollback.
        <Menu.Item
          key="employee-camera-in-out"
          title={<span><IntlMessages id="sidebar.Employee-Camera-In-Out" /></span>}
          className={location.pathname?.includes("/employee-camera-in-out") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/employee-camera-in-out">
            <span><IntlMessages id="sidebar.Employee-Camera-In-Out" /></span>
          </NavLink>
        </Menu.Item>
        */}
        <Menu.Item
          key="resignation-employee-list"
          title={<span><IntlMessages id="sidebar.resignation-employee-list" /></span>}
          className={location.pathname?.includes("/resignation-employee-list") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/resignation-employee-list">
            <span><IntlMessages id="sidebar.resignation-employee-list" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="admin-reports"
          title={<span><IntlMessages id="sidebar.admin-reports" /></span>}
          className={
            ["/admin-reports", "/employee-reports", "/leave-reports", "/attendence-reports", "/letter"].some((path) =>
              location.pathname?.includes(path)
            ) ? "ant-menu-item-active" : ""
          }
        >
          <NavLink to="/admin-reports">
            <span><IntlMessages id="sidebar.admin-reports" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="tickets"
          title={<span><IntlMessages id="sidebar.tickets" /></span>}
          className={location.pathname?.includes("/tickets") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/employee-tickets">
            <span><IntlMessages id="sidebar.tickets" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="admin-leave-approval"
          title={<span><IntlMessages id="sidebar.admin-leave-management" /></span>}
          className={location.pathname?.includes("/admin-leave-approval") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/admin-leave-approval">
            <span><IntlMessages id="sidebar.admin-leave-management" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="pendingtask"
          className={location.pathname?.includes("/pending-task-list") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/pending-task-list">
            <span><IntlMessages id="sidebar.pending-task" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="admin-regularizatrion"
          title={<span><IntlMessages id="sidebar.admin-regularizatrion" /></span>}
          className={location.pathname?.includes("/admin-regularizatrion") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/admin-regularizatrion">
            <span><IntlMessages id="sidebar.admin-regularizatrion" /></span>
          </NavLink>
        </Menu.Item>
      </SubMenu>

      {/* Management SubMenu */}
      <SubMenu
        key="sub17"
        title="Management"
        icon={<PermissionmanagerIcon />}
        popupClassName="right-submenu"
        onMouseEnter={() => handleSubmenuEnter("sub17")}
        onMouseLeave={handleSubmenuLeave}
        className={
          ["/achivements", "/permission-manager", "/admin-department", "/admin-designation", "/source-of-hire-master", "/admin-leave-master", "/employee-leave-balance", "/shift-management", "/meeting-room-master"].some((path) =>
            location.pathname?.includes(path)
          )
            ? "main-submenu full-width-hover ant-menu-item-active"
            : "main-submenu full-width-hover"
        }
        onTitleClick={() => handleTitleClick("sub17")}
      >
        <Menu.Item
          key="achivements"
          title={<span><IntlMessages id="sidebar.achivements" /></span>}
          className={location.pathname?.includes("/achivements") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/achivements">
            <span><IntlMessages id="sidebar.achivements" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="permission"
          title={<span><IntlMessages id="sidebar.permission" /></span>}
          className={location.pathname?.includes("/permission-manager") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/permission-manager">
            <span><IntlMessages id="sidebar.permission" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="admin-department"
          title={<span><IntlMessages id="sidebar.Department" /></span>}
          className={location.pathname?.includes("/admin-department") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/admin-department">
            <span><IntlMessages id="sidebar.Department" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="admin-sub-department"
          title={<span><IntlMessages id="sidebar.SubDepartment" /></span>}
          className={location.pathname?.includes("/admin-sub-department") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/admin-sub-department">
            <span><IntlMessages id="sidebar.SubDepartment" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="admin-designation"
          title={<span><IntlMessages id="sidebar.designation" /></span>}
          className={location.pathname?.includes("/admin-designation") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/admin-designation">
            <span><IntlMessages id="sidebar.designation" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="source-of-hire-master"
          title={<span><IntlMessages id="sidebar.sourceOfHire" /></span>}
          className={location.pathname?.includes("/source-of-hire-master") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/source-of-hire-master">
            <span><IntlMessages id="sidebar.sourceOfHire" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="meeting-room-master"
          title={<span><IntlMessages id="sidebar.MeetingRoomMaster" /></span>}
          className={location.pathname?.includes("/meeting-room-master") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/meeting-room-master">
            <span><IntlMessages id="sidebar.MeetingRoomMaster" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="skills"
          title={<span><IntlMessages id="sidebar.skills" /></span>}
          className={location.pathname?.includes("/skills") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/skills">
            <span><IntlMessages id="sidebar.skills" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="leave-master"
          title={<span><IntlMessages id="sidebar.admin-leave-master" /></span>}
          className={location.pathname?.includes("/admin-leave-master") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/admin-leave-master">
            <span><IntlMessages id="sidebar.admin-leave-master" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="employee-leave-balance"
          title={<span><IntlMessages id="sidebar.employee-leave-balance" /></span>}
          className={location.pathname?.includes("/employee-leave-balance") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/employee-leave-balance">
            <span><IntlMessages id="sidebar.employee-leave-balance" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="shift-management"
          title={<span><IntlMessages id="sidebar.shift-management" /></span>}
          className={location.pathname?.includes("/shift-management") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/shift-management">
            <span><IntlMessages id="sidebar.shift-management" /></span>
          </NavLink>
        </Menu.Item>
      </SubMenu>

      {/* Waffle SubMenu */}
      {getResource(config.RESOURCES.WAFFLE, ["read"]) && (
        <SubMenu
          key="sub21"
          title={<IntlMessages id="Waffle" />}
          icon={<i className="fas fa-stroopwafel" />}
          popupClassName="right-submenu"
          onMouseEnter={() => handleSubmenuEnter("sub21")}
          onMouseLeave={handleSubmenuLeave}
          className={
            ["/Waffle", "/Waffle-summary", "/Waffle-weightage", "/Waffle-category", "/Waffle-reward-approvel"].some((path) =>
              location.pathname?.includes(path.replace("/", ""))
            ) || ["/Waffle", "/Waffle-summary", "/Waffle-weightage", "/Waffle-category", "/Waffle-reward-approvel"].includes(location.pathname)
              ? "main-submenu full-width-hover ant-menu-item-active"
              : "main-submenu full-width-hover"
          }
          onTitleClick={() => handleTitleClick("sub21")}
        >
          <Menu.Item
            key="karma-send"
            title={<span><IntlMessages id="Waffle Dashboard" /></span>}
            className={location.pathname === "/Waffle" ? "ant-menu-item-active" : ""}
          >
            <NavLink to="/Waffle">
              <span><IntlMessages id="sidebar.karmaSend" /></span>
            </NavLink>
          </Menu.Item>
          <Menu.Item
            key="karma-summary"
            title={<span><IntlMessages id="Waffle Summary" /></span>}
            className={location.pathname === "/Waffle-summary" ? "ant-menu-item-active" : ""}
          >
            <NavLink to="/Waffle-summary">
              <span><IntlMessages id="sidebar.karmaSummary" /></span>
            </NavLink>
          </Menu.Item>
          {getResource(config.RESOURCES.WAFFLE_WEIGHTAGE, ["read"]) && (
            <Menu.Item
              key="karma-weightage"
              title={<span><IntlMessages id="sidebar.karmaWeightage" /></span>}
              className={location.pathname === "/Waffle-weightage" ? "ant-menu-item-active" : ""}
            >
              <NavLink to="/Waffle-weightage">
                <span><IntlMessages id="sidebar.karmaWeightage" /></span>
              </NavLink>
            </Menu.Item>
          )}
          {getResource(config.RESOURCES.WAFFLE, ["create"]) && (
            <Menu.Item
              key="karma-karmaCategory"
              title={<span><IntlMessages id="sidebar.karmaCategory" /></span>}
              className={location.pathname === "/Waffle-category" ? "ant-menu-item-active" : ""}
            >
              <NavLink to="/Waffle-category">
                <span><IntlMessages id="sidebar.karmaCategory" /></span>
              </NavLink>
            </Menu.Item>
          )}
          {getResource(config.RESOURCES.WAFFLE_REWARD, ["read"]) && (
            <Menu.Item
              key="karma-reward-approvel"
              title={<span><IntlMessages id="sidebar.karmaRewardApprovel" /></span>}
              className={location.pathname === "/Waffle-reward-approvel" ? "ant-menu-item-active" : ""}
            >
              <NavLink to="/Waffle-reward-approvel">
                <span><IntlMessages id="sidebar.karmaRewardApprovel" /></span>
              </NavLink>
            </Menu.Item>
          )}
        </SubMenu>
      )}

      {/* Onboarding SubMenu */}
      <SubMenu
        key="sub19"
        title="Onboarding"
        icon={<OnboardingIcon />}
        popupClassName="right-submenu"
        onMouseEnter={() => handleSubmenuEnter("sub19")}
        onMouseLeave={handleSubmenuLeave}
        className={
          ["/candidate-approval", "/candidate-list", "/onboarding-initiation", "/doc-approve-list", "/new-joinees-list"].some((path) =>
            location.pathname?.includes(path)
          )
            ? "main-submenu full-width-hover ant-menu-item-active"
            : "main-submenu full-width-hover"
        }
        onTitleClick={() => handleTitleClick("sub19")}
      >
        <Menu.Item
          key="candidate-approvel"
          title={<span><IntlMessages id="sidebar.candidate-approvel" /></span>}
          className={location.pathname?.includes("/candidate-approval") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/candidate-approval">
            <span><IntlMessages id="sidebar.candidate-approvel" /></span>
          </NavLink>
        </Menu.Item>
        {getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"]) && (
          <Menu.Item
            key="candidate-list"
            title={<span><IntlMessages id="sidebar.candidate-approvel-list" /></span>}
            className={location.pathname?.includes("/candidate-list") ? "ant-menu-item-active" : ""}
          >
            <NavLink to="/candidate-list">
              <span><IntlMessages id="sidebar.candidate-approvel-list" /></span>
            </NavLink>
          </Menu.Item>
        )}
        <Menu.Item
          key="initiation"
          title={<span><IntlMessages id="sidebar.onboarding-initiation" /></span>}
          className={location.pathname?.includes("/onboarding-initiation") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/onboarding-initiation">
            <span><IntlMessages id="sidebar.onboarding-initiation" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="doc-approve-list"
          title={<span><IntlMessages id="sidebar.doc-approve-list" /></span>}
          className={location.pathname?.includes("/doc-approve-list") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/doc-approve-list">
            <span><IntlMessages id="sidebar.doc-approve-list" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="new-joinees-list"
          title={<span><IntlMessages id="sidebar.new-joinees-list" /></span>}
          className={location.pathname?.includes("/new-joinees-list") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/new-joinees-list">
            <span><IntlMessages id="sidebar.new-joinees-list" /></span>
          </NavLink>
        </Menu.Item>
      </SubMenu>

      {/* Audit SubMenu */}
      <SubMenu
        key="sub20"
        title="Audit"
        icon={<AuditIcon />}
        popupClassName="right-submenu"
        onMouseEnter={() => handleSubmenuEnter("sub20")}
        onMouseLeave={handleSubmenuLeave}
        className={
          ["/auditmaster", "/audits", "/auditperformance", "/audithead"].some((path) =>
            location.pathname?.includes(path)
          )
            ? "main-submenu full-width-hover ant-menu-item-active"
            : "main-submenu full-width-hover"
        }
        onTitleClick={() => handleTitleClick("sub20")}
      >
        <Menu.Item
          key="Audit Master"
          title={<span><IntlMessages id="Audits Master" /></span>}
          className={location.pathname?.includes("/auditmaster") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/auditmaster">
            <span><IntlMessages id="Audits Master" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="Audits"
          title={<span><IntlMessages id="Audits" /></span>}
          className={location.pathname?.includes("/audits") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/audits">
            <span><IntlMessages id="Audits" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="auditperformance"
          title={<span><IntlMessages id="Audit Performance" /></span>}
          className={location.pathname?.includes("/auditperformance") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/auditperformance">
            <span><IntlMessages id="Audit Performance" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="auditHead"
          title={<span><IntlMessages id="Audit Head" /></span>}
          className={location.pathname?.includes("/audithead") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/audithead">
            <span><IntlMessages id="Audit Head" /></span>
          </NavLink>
        </Menu.Item>
      </SubMenu>

      {/* KPI / KRA SubMenu */}
      {(getResource(config.RESOURCES.KPI_KRA, ["read"]) ||
        getResource(config.RESOURCES.KPI_REVIEWER, ["read"]) ||
        getResource(config.RESOURCES.KPI_EMPLOYEE, ["read"])) && (
        <SubMenu
          key="sub-kpi-admin"
          title="KPI / KRA"
          icon={<GiOnTarget />}
          popupClassName="right-submenu"
          onMouseEnter={() => handleSubmenuEnter("sub-kpi-admin")}
          onMouseLeave={handleSubmenuLeave}
          className={
            !location.pathname?.startsWith("/performance/") &&
            ["/kpi-kra", "/reviewer-panel", "/kpis"].some((path) =>
              location.pathname?.includes(path)
            )
              ? "main-submenu full-width-hover ant-menu-item-active"
              : "main-submenu full-width-hover"
          }
          onTitleClick={() => handleTitleClick("sub-kpi-admin")}
        >
          {getResource(config.RESOURCES.KPI_KRA, ["read"]) && (
            <Menu.Item
              key="kpi-kra"
              className={!location.pathname?.startsWith("/performance/") && location.pathname?.includes("/kpi-kra") ? "ant-menu-item-active" : ""}
            >
              <NavLink to="/kpi-kra">
                <span>KPI / KRA</span>
              </NavLink>
            </Menu.Item>
          )}
          {getResource(config.RESOURCES.KPI_REVIEWER, ["read"]) && (
            <Menu.Item
              key="reviewer-panel"
              className={location.pathname?.includes("/reviewer-panel") ? "ant-menu-item-active" : ""}
            >
              <NavLink to="/reviewer-panel">
                <span>KPI Reviewer</span>
              </NavLink>
            </Menu.Item>
          )}
          {getResource(config.RESOURCES.KPI_EMPLOYEE, ["read"]) && (
            <Menu.Item
              key="kpis"
              className={location.pathname?.includes("/kpis") ? "ant-menu-item-active" : ""}
            >
              <NavLink to="/kpis">
                <span>My KPIs</span>
              </NavLink>
            </Menu.Item>
          )}
        </SubMenu>
      )}

      {/* Performance SubMenu */}
      {(getResource(config.RESOURCES.PERFORMANCE_DASHBOARD, ["read"]) ||
        getResource(config.RESOURCES.PERFORMANCE_CYCLES, ["read"]) ||
        getResource(config.RESOURCES.PERFORMANCE_TEMPLATES, ["read"]) ||
        getResource(config.RESOURCES.PERFORMANCE_GOALS, ["read"]) ||
        getResource(config.RESOURCES.PERFORMANCE_KPI_KRA, ["read"]) ||
        getResource(config.RESOURCES.PERFORMANCE_APPRAISAL, ["read"]) ||
        getResource(config.RESOURCES.PERFORMANCE_REPORTS, ["read"])) && (
        <SubMenu
          key="sub-performance-admin"
          title="Performance"
          icon={<FaChartLine />}
          popupClassName="right-submenu"
          onMouseEnter={() => handleSubmenuEnter("sub-performance-admin")}
          onMouseLeave={handleSubmenuLeave}
          className={
            location.pathname?.includes("/performance/")
              ? "main-submenu full-width-hover ant-menu-item-active"
              : "main-submenu full-width-hover"
          }
          onTitleClick={() => handleTitleClick("sub-performance-admin")}
        >
          {getResource(config.RESOURCES.PERFORMANCE_DASHBOARD, ["read"]) && (
            <Menu.Item key="performance-dashboard" className={location.pathname?.includes("/performance/dashboard") ? "ant-menu-item-active" : ""}>
              <NavLink to="/performance/dashboard"><span>Dashboard</span></NavLink>
            </Menu.Item>
          )}
          {getResource(config.RESOURCES.PERFORMANCE_CYCLES, ["read"]) && (
            <Menu.Item key="performance-cycles" className={location.pathname?.includes("/performance/cycles") ? "ant-menu-item-active" : ""}>
              <NavLink to="/performance/cycles"><span>Performance Cycles</span></NavLink>
            </Menu.Item>
          )}
          {getResource(config.RESOURCES.PERFORMANCE_TEMPLATES, ["read"]) && (
            <Menu.Item key="performance-templates" className={location.pathname?.includes("/performance/templates") ? "ant-menu-item-active" : ""}>
              <NavLink to="/performance/templates"><span>Appraisal Templates</span></NavLink>
            </Menu.Item>
          )}
          {getResource(config.RESOURCES.PERFORMANCE_GOALS, ["read"]) && (
            <Menu.Item key="performance-goals" className={location.pathname?.includes("/performance/goals") ? "ant-menu-item-active" : ""}>
              <NavLink to="/performance/goals"><span>Goals</span></NavLink>
            </Menu.Item>
          )}
          {getResource(config.RESOURCES.PERFORMANCE_KPI_KRA, ["read"]) && (
            <Menu.Item key="performance-kpi-kra" className={location.pathname?.includes("/performance/kpi-kra") ? "ant-menu-item-active" : ""}>
              <NavLink to="/performance/kpi-kra"><span>KPI / KRA</span></NavLink>
            </Menu.Item>
          )}
          {getResource(config.RESOURCES.PERFORMANCE_APPRAISAL, ["read"]) && (
            <Menu.Item key="performance-appraisal" className={location.pathname?.includes("/performance/appraisal") ? "ant-menu-item-active" : ""}>
              <NavLink to="/performance/appraisal"><span>Appraisal</span></NavLink>
            </Menu.Item>
          )}
          {getResource(config.RESOURCES.PERFORMANCE_REPORTS, ["read"]) && (
            <Menu.Item key="performance-reports" className={location.pathname?.includes("/performance/reports") ? "ant-menu-item-active" : ""}>
              <NavLink to="/performance/reports"><span>Reports</span></NavLink>
            </Menu.Item>
          )}
        </SubMenu>
      )}

      {/* More SubMenu */}
      <SubMenu
        key="sub18"
        title="More"
        icon={<i className="fas fa-ellipsis-h" />}
        popupClassName="right-submenu"
        onMouseEnter={() => handleSubmenuEnter("sub18")}
        onMouseLeave={handleSubmenuLeave}
        className={
          !location.pathname?.startsWith("/performance/") &&
          ["/holidays", "/subscription", "/bill-reimbursement", "/policies", "/goals", "/jobs/openings", "/inventory"].some((path) =>
            location.pathname?.includes(path)
          )
            ? "main-submenu full-width-hover ant-menu-item-active"
            : "main-submenu full-width-hover"
        }
        onTitleClick={() => handleTitleClick("sub18")}
      >

        <Menu.Item
          key="mood-analytics"
          title={<span><IntlMessages id="mood-analytics" /></span>}
          className={location.pathname?.includes("/mood-list") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/mood-list">
            <span><IntlMessages id="Mood Analytics" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="holidays"
          title={<span><IntlMessages id="holidays" /></span>}
          className={location.pathname?.includes("/holidays") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/holidays">
            <span><IntlMessages id="Holidays" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="subscription"
          title={<span><IntlMessages id="sidebar.subscription" /></span>}
          className={location.pathname?.includes("/subscription") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/subscription">
            <span><IntlMessages id="sidebar.subscription" /></span>
          </NavLink>
        </Menu.Item>
        {(getResource(config.RESOURCES.BILLREIMBURSEMENT, ["read"]) ||
          DEPARTMENT_ARRAY.includes(authUser?.subdepartment_id._id?.toString())) && (
            <Menu.Item
              key="Bill-Reimbursement"
              className={location.pathname?.includes("/bill-reimbursement") ? "ant-menu-item-active" : ""}
            >
              <NavLink to="/bill-reimbursement">
                <span><IntlMessages id="sidebar.bill-reimbursement" /></span>
              </NavLink>
            </Menu.Item>
          )}
        <Menu.Item
          key="policies"
          title={<span><IntlMessages id="sidebar.policies" /></span>}
          className={location.pathname?.includes("/policies") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/policies">
            <span><IntlMessages id="sidebar.policies" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="goals"
          title={<span><IntlMessages id="sidebar.create-goals" /></span>}
          className={!location.pathname?.startsWith("/performance/") && location.pathname?.includes("/goals") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/goals">
            <span><IntlMessages id="sidebar.create-goals" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="Job Opening"
          title={<span><IntlMessages id="sidebar.admin-jobopening" /></span>}
          className={location.pathname?.includes("/jobs/openings") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/jobs/openings">
            <span><IntlMessages id="sidebar.admin-jobopening" /></span>
          </NavLink>
        </Menu.Item>
        <Menu.Item
          key="inventory"
          title={<span><IntlMessages id="sidebar.user-assetstabpane" /></span>}
          className={location.pathname?.includes("/inventory") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/inventory">
            <span><IntlMessages id="sidebar.user-assetstabpane" /></span>
          </NavLink>
        </Menu.Item>

        <Menu.Item
          key="appsettings"
          title={<span><IntlMessages id="sidebar.app-setting" /></span>}
          className={location.pathname?.includes("/app-setting") ? "ant-menu-item-active" : ""}
        >
          <NavLink to="/app-setting">
            {/* <FaRegSun /> */}
            <span><IntlMessages id="sidebar.app-setting" /></span>
          </NavLink>
        </Menu.Item>

      </SubMenu>

      {/* Organization Tree */}
      <Menu.Item
        key="organisationtree"
        title={<span><IntlMessages id="sidebar.organisation-tree" /></span>}
        className={location.pathname?.includes("/organization-view") ? "ant-menu-item-active" : ""}
      >
        <NavLink to="/organization-view">
          <ImTree />
          <span><IntlMessages id="sidebar.organisation-tree" /></span>
        </NavLink>
      </Menu.Item>
{(userdata?._id == '63b6ca9270f2b5b3002cb552' || userdata?._id == '68b5673ddbd1f7003fef2c81' || userdata?._id == '63b6ca8670f2b5b3002cb433')  &&
       <Menu.Item
        key="MIRA"
        title={<span><IntlMessages id="MIRA" /></span>}
        className={location.pathname?.includes("/mira") ? "ant-menu-item-active" : ""}
      >
        <NavLink to="/mira">
          <AiOutlineDashboard />
          <span>
            <IntlMessages id="sidebar.AI" />
          </span>
        </NavLink>
      </Menu.Item>
}
    </Menu>
  );

  // Get current user role and corresponding menu config
  const getCurrentRole = () => {
    for (const role of Object.keys(ROLE_CONFIGS)) {
      if (getRoles([role])) {
        return role;
      }
    }
    return null;
  };

  // Render role-based menu
  const renderRoleBasedMenu = () => {
    const currentRole = getCurrentRole();

    if (currentRole === "Admin") {
      return renderAdminMenu();
    }

    if (currentRole && ROLE_CONFIGS[currentRole]) {
      const menuConfig = ROLE_CONFIGS[currentRole];
      return (
        <Menu {...menuProps}>
          {renderMenuFromConfig(menuConfig)}
        </Menu>
      );
    }

    return null;
  };

  // Render sidebar content
  const renderSidebarContent = () => {
    const menu = renderRoleBasedMenu();

    if (!menu) return null;

    return (
      <div className="gx-sidebar-content sidebar-menu">
        <CustomScrollbars className="gx-layout-sider-scrollbar">
          {menu}
        </CustomScrollbars>
      </div>
    );
  };

  return (
    <Sider
      className="Sidebar"
      width={120}
      style={{ minWidth: '120px', maxWidth: '120px' }}
    >
      <SidebarLogo />
      {renderSidebarContent()}
    </Sider>
  );
};

export default SidebarContent;